/**
 * AdminFirmRoster — staff-operated firm roster (admin dashboard §5).
 *
 * Company-staff/owner tool (the backend endpoints are require_role("admin")) to view a
 * firm's Clerk-org attorneys + pending invites and invite/remove members. Respects the
 * locked 2026-07-12 provisioning model (staff-invite only; no attorney-facing firm-admin
 * role) — this is NOT a self-serve firm-admin surface. Rendered inside AdminFirmCases.
 *
 * Coded by kimi 2.6 to a conductor-verified spec; conductor-reviewed (api.delete body/opts
 * arg order, inviteDisabled-when-unconnected, token/badge style match with AdminFirmCases).
 *
 * Endpoints:
 *   GET    /api/v1/law-firms/{firmId}/roster
 *   POST   /api/v1/law-firms/{firmId}/attorneys/invite  { email }
 *   DELETE /api/v1/law-firms/{firmId}/attorneys/{clerk_user_id}
 */
import React, { useEffect, useState } from 'react';
import { Loader2, UserPlus, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';

interface RosterMember {
  clerk_user_id: string;
  email: string;
  name: string;
  role: string;
  membership_id: string;
  created_at: string;
}

interface PendingInvitation {
  invitation_id: string;
  email: string;
  role: string;
  status: string;
  created_at: string;
}

interface FirmRoster {
  law_firm_id: string;
  clerk_org_id: string | null;
  connected: boolean;
  members: RosterMember[];
  pending_invitations: PendingInvitation[];
}

// ISO provenance dates (K3 design gate 2026-10-09): YYYY-MM-DD, monospace/tabular.
const fmtDate = (d?: string) => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export default function AdminFirmRoster({ firmId }: { firmId: string }) {
  const { getToken } = useAuth();
  const [roster, setRoster] = useState<FirmRoster | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  const loadRoster = async () => {
    if (!firmId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const data = await api.get<FirmRoster>(`/api/v1/law-firms/${firmId}/roster`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoster(data);
    } catch (e: any) {
      setError(e?.message || 'Could not load roster.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!firmId) return;
    loadRoster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmId]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    if (!roster?.connected) return;

    setBusy(true);
    setActionMsg(null);
    try {
      const token = await getToken();
      await api.post<{ invitation_id: string; email: string }>(
        `/api/v1/law-firms/${firmId}/attorneys/invite`,
        { email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInviteEmail('');
      setActionMsg(`Invited ${email}`);
      await loadRoster();
    } catch (e: any) {
      setActionMsg(e?.message || 'Invite failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (member: RosterMember) => {
    const displayName = member.name || member.email;
    if (!window.confirm(`Remove ${displayName} from this firm?`)) return;

    setBusy(true);
    setActionMsg(null);
    try {
      const token = await getToken();
      await api.delete<{ ok: boolean }>(
        `/api/v1/law-firms/${firmId}/attorneys/${member.clerk_user_id}`,
        undefined,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActionMsg(`Removed ${displayName}`);
      await loadRoster();
    } catch (e: any) {
      setActionMsg(e?.message || 'Remove failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async (inv: PendingInvitation) => {
    setBusy(true);
    setActionMsg(null);
    try {
      const token = await getToken();
      await api.post(
        `/api/v1/law-firms/${firmId}/invitations/${inv.invitation_id}/resend`,
        { email: inv.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActionMsg(`Resent invite to ${inv.email}`);
      await loadRoster();
    } catch (e: any) {
      setActionMsg(e?.message || 'Resend failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (inv: PendingInvitation) => {
    if (!window.confirm(`Revoke the pending invite to ${inv.email}?`)) return;
    setBusy(true);
    setActionMsg(null);
    try {
      const token = await getToken();
      await api.delete(
        `/api/v1/law-firms/${firmId}/invitations/${inv.invitation_id}`,
        undefined,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActionMsg(`Revoked invite to ${inv.email}`);
      await loadRoster();
    } catch (e: any) {
      setActionMsg(e?.message || 'Revoke failed.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
      </div>
    );
  }

  if (error) {
    return (
      <p className="py-10 text-center text-sm" style={{ color: 'var(--color-needs-human-text)' }}>{error}</p>
    );
  }

  const metaText = roster?.connected
    ? `${roster.members.length} ATTORNEY${roster.members.length === 1 ? '' : 'S'} · ${roster.pending_invitations.length} PENDING`
    : 'Not connected to a Clerk organization yet.';

  const inviteDisabled = busy || (roster !== null && roster.connected === false);

  return (
    <section className="space-y-6">
      <div>
        <h2 className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>Firm roster</h2>
        <p className={`cs-meta mt-1 ${roster?.connected ? 'font-mono uppercase tracking-wide' : ''}`} style={{ color: 'var(--color-text-muted)' }}>{metaText}</p>
      </div>

      <form onSubmit={handleInvite} className="flex items-center gap-3 flex-wrap">
        <input
          type="email"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          placeholder="attorney@firm.com"
          disabled={inviteDisabled}
          className="flex-1 min-w-[12rem] rounded px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[var(--color-needs-human-text)]"
          style={{
            background: 'var(--color-bg-elevated)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-text-primary)',
          }}
        />
        <button
          type="submit"
          disabled={inviteDisabled}
          className="inline-flex items-center gap-2 rounded px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50"
          style={{
            border: '1px solid var(--color-needs-human-text)',
            color: 'var(--color-needs-human-text)',
            background: 'transparent',
          }}
        >
          <UserPlus className="w-4 h-4" />
          Invite
        </button>
        {roster && !roster.connected && (
          <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>
            Connect this firm to a Clerk org first
          </span>
        )}
      </form>

      {actionMsg && (
        <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>{actionMsg}</p>
      )}

      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)' }}>
        {roster && (roster.members.length > 0 || roster.pending_invitations.length > 0) ? (
          <>
            {roster.members.map((m, i) => {
            const displayName = m.name || m.email;
            const isAdmin = m.role === 'org:admin';
            const joinedDate = fmtDate(m.created_at);
            return (
              <div
                key={m.clerk_user_id}
                className="flex items-center gap-4 px-5 py-4"
                style={{ borderTop: i ? '1px solid var(--color-border)' : 'none' }}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      {displayName}
                    </span>
                    <span
                      className="font-mono text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded"
                      style={{
                        color: isAdmin ? 'var(--color-needs-human-text)' : 'var(--color-text-muted)',
                        border: `1px solid ${isAdmin ? 'var(--color-needs-human-text)' : 'var(--color-text-muted)'}`,
                      }}
                    >
                      {m.role.replace('org:', '')}
                    </span>
                  </div>
                  <div className="mt-1 text-xs font-mono" style={{ color: 'var(--color-text-dim)' }}>
                    {m.email}
                  </div>
                </div>
                {joinedDate && (
                  <span
                    className="flex-shrink-0 mr-3 font-mono text-[10px] uppercase tracking-wide tabular-nums"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    Joined {joinedDate}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => handleRemove(m)}
                  disabled={busy}
                  className="flex-shrink-0 p-1.5 rounded transition-colors disabled:opacity-50 hover:text-[var(--color-text-primary)]"
                  style={{ color: 'var(--color-text-muted)' }}
                  aria-label={`Remove ${displayName}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
            })}
            {roster.pending_invitations.length > 0 && (
              <>
                <div
                  className="px-5 py-2"
                  style={{ borderTop: roster.members.length ? '1px solid var(--color-border)' : 'none' }}
                >
                  <span className="font-mono text-[10px] uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                    Pending — {roster.pending_invitations.length}
                  </span>
                </div>
                {roster.pending_invitations.map((inv) => (
                  <div
                    key={inv.invitation_id}
                    className="flex items-center gap-3 px-5 py-3"
                    style={{ borderTop: '1px solid var(--color-border)' }}
                  >
                    <span className="text-sm flex-1 min-w-0 truncate" style={{ color: 'var(--color-text-primary)' }}>{inv.email}</span>
                    <span
                      className="font-mono text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0"
                      style={{ color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}
                    >
                      pending
                    </span>
                    <button
                      type="button"
                      onClick={() => handleResend(inv)}
                      disabled={busy}
                      className="flex-shrink-0 font-mono text-[10px] uppercase tracking-wide transition-colors disabled:opacity-50 hover:text-[var(--color-text-primary)]"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      Resend
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRevoke(inv)}
                      disabled={busy}
                      className="flex-shrink-0 p-1.5 rounded transition-colors disabled:opacity-50 hover:text-[var(--color-text-primary)]"
                      style={{ color: 'var(--color-text-muted)' }}
                      aria-label={`Revoke invite to ${inv.email}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </>
            )}
          </>
        ) : (
          <p className="py-12 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
            No attorneys yet.
          </p>
        )}
      </div>
    </section>
  );
}
